/**
 * Resolves a short role name ("male_equity"/"female_crypto") to its
 * `RoleLocation` (the real contract `organism_id`, used to build fetch
 * paths). `fleet.json` is authoritative when published (owner requirement
 * 2026-09-26: "drive the fleet screen and the organism routes from
 * fleet.json"); `src/config.ts`'s `defaultRoles` is the fallback for a
 * fresh deployment/fixture where `fleet.json` has not appeared yet.
 */

import { config, roleLabel } from "../config";
import { loadFleetSnapshot } from "./fleet";
import type { RoleLocation } from "./snapshotIndex";
import type { FleetSnapshot } from "../types/incremental";

let fleetPromise: Promise<FleetSnapshot | null> | null = null;

/** Exported (code-review finding, revamp self-review round 1) so a caller
 * that needs the raw `FleetSnapshot` alongside `resolveAllRoleLocations()`
 * — the fleet-bar organism switcher's per-pill status dot, `App.ts` — reads
 * the same in-flight/resolved promise instead of issuing its own separate
 * `loadFleetSnapshot()` fetch for the same URL on every page load. Cached
 * for the page's lifetime by design (this module resolves organism routes
 * for the whole session); `FleetScreen.ts` intentionally does NOT use this
 * — a visit to the fleet screen itself should always re-fetch for fresh
 * NAV/heartbeat data, not read a load-time-only snapshot. */
export function loadFleetOnce(): Promise<FleetSnapshot | null> {
  fleetPromise ??= loadFleetSnapshot();
  return fleetPromise;
}

function fallbackLocation(role: string): RoleLocation {
  const fallback = config.defaultRoles.find((entry) => entry.role === role);
  return {
    role,
    organismId: fallback?.organismId ?? role,
    displayName: fallback?.displayName ?? roleLabel(role),
    path: fallback?.organismId ?? role,
  };
}

export async function resolveRoleLocation(role: string): Promise<RoleLocation> {
  const fleet = await loadFleetOnce();
  const entry = fleet?.organisms.find((organism) => organism.role === role);
  if (!entry) return fallbackLocation(role);
  return {
    role: entry.role,
    organismId: entry.organism_id,
    displayName: roleLabel(entry.role),
    path: entry.organism_id,
  };
}

/** All roles known to `fleet.json` (falling back to `config.defaultRoles`
 * when it has not been published yet) — used to build the fleet bar's nav. */
export async function resolveAllRoleLocations(): Promise<readonly RoleLocation[]> {
  const fleet = await loadFleetOnce();
  if (fleet && fleet.organisms.length > 0) {
    return fleet.organisms.map((entry) => ({
      role: entry.role,
      organismId: entry.organism_id,
      displayName: roleLabel(entry.role),
      path: entry.organism_id,
    }));
  }
  return config.defaultRoles.map((entry) => ({
    role: entry.role,
    organismId: entry.organismId,
    displayName: entry.displayName,
    path: entry.organismId,
  }));
}

/** Test-only: forces the next call to re-fetch fleet.json. */
export function resetRoleDirectoryForTests(): void {
  fleetPromise = null;
}
